'use strict';

const toMarkdown = require('to-markdown');
const marked = require('marked');

/** Survives to-markdown + marked; invisible in the editor. */
const EMPTY_P = '\u200b';

const options = {
	toMarkdown: {
		converters: [
			{
				filter: 'li',
				replacement: (content, node) => {
					content = content.replace(/^\s+/, '').replace(/\n/gm, '\n ');
					let prefix = '- ';
					const parent = node.parentNode;
					const index = Array.prototype.indexOf.call(parent.children, node) + 1;

					prefix = /ol/i.test(parent.nodeName) ? index + '. ' : '- ';
					return prefix + content;
				}
			},
			// Chrome wraps typed text in <span style="font-size:…"> — never keep that in markdown
			{
				filter: 'span',
				replacement: content => content
			},
			{
				// Blank paragraphs (Enter at end of doc) — keep as empty lines in markdown
				filter: (node) => {
					if (node.nodeName !== 'P') return false;
					if (node.parentNode && node.parentNode.tagName === 'LI') return false;
					const text = (node.textContent || '').replace(/\u00a0/g, '');
					return text === '' || text === EMPTY_P;
				},
				replacement: () => `\n\n${EMPTY_P}\n\n`
			},
			{
				filter: 'p',
				replacement: (content, node) => node.parentNode.tagName !== 'LI'
					? `\n\n${content}` : content
			}
		]
	},
	marked: {
		gfm: true,
		breaks: false,
		smartLists: true
	}
};

marked.setOptions(options.marked);

/**
 * Strip contenteditable chrome before markdown:
 * empty / font-size spans, leftover style wrappers.
 */
const stripEditorNoise = (html) => {
	let out = String(html || '');
	let prev;
	do {
		prev = out;
		out = out
			.replace(/<span\b[^>]*>\s*<\/span>/gi, '')
			.replace(/<span\b[^>]*\sstyle\s*=\s*("[^"]*"|'[^']*')[^>]*>([\s\S]*?)<\/span>/gi, '$2')
			.replace(/<\/?font\b[^>]*>/gi, '');
	} while (out !== prev);
	return out;
};

/** Turn empty / <br>-only blocks into a placeholder to-markdown will keep. */
const preserveEmptyBlocks = html =>
	String(html || '')
		.replace(/<div>/gi, '<p>')
		.replace(/<\/div>/gi, '</p>')
		.replace(/<p\b[^>]*>(?:\s|&nbsp;|<br\s*\/?>)*<\/p>/gi, `<p>${EMPTY_P}</p>`);

const toHTML = md => (
	marked.parse(md || '')
		.replace(/<div>/ig, '<p>')
		.replace(/<\/div>/ig, '</p>')
		.replace(/<li><p>/ig, '<li>')
		.replace(/<\/p>(\n)?<\/li>/ig, '</li>')
		// contenteditable prefers <br> in blank lines for caret placement
		.replace(new RegExp(`<p>${EMPTY_P}</p>`, 'g'), '<p><br></p>')
		.replace(/<p><\/p>/ig, '')
);

/** Public display: no editor empty `<p><br></p>`, no `<br>` inside paragraphs. */
const toDisplayHTML = source => {
	let html = toHTML(source)
		.replace(/<p>\s*(?:<br\s*\/?>|&nbsp;|\u200b|\s)*<\/p>/gi, '');
	html = html.replace(/<p\b[^>]*>([\s\S]*?)<\/p>/gi, (_, inner) => {
		if (!/<br\s*\/?>/i.test(inner)) {
			return `<p>${inner}</p>`;
		}
		return String(inner)
			.split(/<br\s*\/?>/i)
			.map(chunk => chunk.trim())
			.filter(Boolean)
			.map(chunk => `<p>${chunk}</p>`)
			.join('');
	});
	return html;
};

const fromHTML = html =>
	toMarkdown(preserveEmptyBlocks(stripEditorNoise(html)), options.toMarkdown);

module.exports = {
	options,
	toHTML,
	toDisplayHTML,
	fromHTML
};
